import React, { useCallback } from "react";
import Typewriter from "typewriter-effect";
import { useNavigate } from "react-router-dom";

import {
  // root
  HeroRoot,
  // eyebrow
  EyebrowChip,
  // headline
  StaticTitle, TypeLine,
  // divider
  TitleDivider,
  // subtitle
  SubTitle,
  // cta
  ButtonRow, PrimaryBtn, GhostBtn,
  // trust
  TrustRow, TrustItem, TrustSep,
  // stats
  StatsStrip, StatCard, StatValue, StatLabel,
} from "./TypeWriterText.styles";

// ─── Icon atoms (inline SVG — zero deps, zero network) ─────────────────────────

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5"  y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
  </svg>
);

// ─── Typewriter word sequences ─────────────────────────────────────────────────

const WORDS = [
  { text: "Mindfulness.",  cls: "tw-teal"    },
  { text: "Strength.",     cls: "tw-saffron" },
  { text: "Flexibility.",  cls: "tw-rose"    },
  { text: "Relaxation.",   cls: "tw-teal"    },
  { text: "Balance.",      cls: "tw-saffron" },
  { text: "Harmony.",      cls: "tw-rose"    },
  { text: "Wellness.",     cls: "tw-teal"    },
  { text: "Energy.",       cls: "tw-saffron" },
  { text: "Serenity.",     cls: "tw-rose"    },
  { text: "Inner peace.",  cls: "tw-teal"    },
];

// Built once at module level — no re-creation on renders
const buildTypewriter = (typewriter) => {
  WORDS.forEach(({ text, cls }) => {
    typewriter
      .typeString(`<span class="${cls}">${text}</span>`)
      .pauseFor(1800)
      .deleteAll(28);
  });
  typewriter.start();
};

// ─── Static data ───────────────────────────────────────────────────────────────

const STATS = [
  { value: "2,400+", label: "Students",    delay: 600 },
  { value: "40+",    label: "Instructors", delay: 680 },
  { value: "12+",    label: "Styles",      delay: 760 },
];

const TRUST_ITEMS = [
  { icon: "✦", text: "All levels welcome",  delay: 520 },
  { icon: "✦", text: "Live Online Sessions", delay: 580 },
  { icon: "✦", text: "Personalized Guidance",      delay: 640 },
];

// ─── Component ────────────────────────────────────────────────────────────────

const TypeWriterText = () => {
  const navigate    = useNavigate();
  const handleBook  = useCallback(() => navigate("/contactUs"), [navigate]);
  const handleLearn = useCallback(() => navigate("/#about"),    [navigate]);

  return (
    <HeroRoot>

      {/* ── Live-class indicator chip ── */}
      <EyebrowChip aria-label="Online yoga classes — now live">
        <div className="chip-dot-wrap" aria-hidden="true">
          <div className="chip-ring" />
          <div className="chip-dot"  />
        </div>
        <span className="chip-label">Online Yoga Classes · Now Live</span>
      </EyebrowChip>

      {/* ── Static headline ── */}
        
      <StaticTitle>
        PERSONALIZED ONLINE YOGA · LIVE WITH <em>EXPERT</em> INSTRUCTORS.
      </StaticTitle>

      {/* ── Animated typewriter line ── */}
      <TypeLine aria-live="polite" aria-atomic="true">
        <Typewriter
          options={{
            autoStart:   true,
            loop:        true,
            delay:       52,
            deleteSpeed: 28,
          }}
          onInit={buildTypewriter}
        />
      </TypeLine>

      {/* ── Tri-colour gradient rule ── */}
      <TitleDivider aria-hidden="true" />

      {/* ── Descriptor copy ── */}
      <SubTitle>
        Practice yoga from anywhere with personalized guidance based on your goals, experience, body, and lifestyle. Whether you're a beginner or an experienced practitioner, our instructors help you build a practice that works for you.
      </SubTitle>

      {/* ── CTA row ── */}
      <ButtonRow>
        <PrimaryBtn
          type="button"
          onClick={handleBook}
          aria-label="Book a free trial yoga class"
        >
          Book a Free Trial <ArrowIcon />
        </PrimaryBtn>

        {/* <GhostBtn
          type="button"
          onClick={handleLearn}
          aria-label="Watch how it works"
        >
          <PlayIcon /> How it works
        </GhostBtn> */}
      </ButtonRow>

      {/* ── Social proof trust row ── */}
      <TrustRow aria-label="Why students choose us">
        {TRUST_ITEMS.map(({ icon, text, delay }, i) => (
          <React.Fragment key={text}>
            <TrustItem $delay={delay}>
              <span className="trust-icon" aria-hidden="true">{icon}</span>
              {text}
            </TrustItem>
            {i < TRUST_ITEMS.length - 1 && <TrustSep aria-hidden="true" />}
          </React.Fragment>
        ))}
      </TrustRow>

      {/* ── Stat cards ── */}
      <StatsStrip aria-label="Platform statistics">
        {STATS.map(({ value, label, delay }) => (
          <StatCard key={label} $delay={delay}>
            <StatValue>{value}</StatValue>
            <StatLabel>{label}</StatLabel>
          </StatCard>
        ))}
      </StatsStrip>

    </HeroRoot>
  );
};

TypeWriterText.displayName = "TypeWriterText";
export default React.memo(TypeWriterText);